import { MismatchWarning, OnboardingContext } from '../../../models';
import { BillNavEntry } from '../state/bill-ai-flow.models';

const normalize = (s: string | undefined | null): string => (s || '').trim().toLowerCase();

/** Comma-separated address compare key: parts trimmed individually and empty
 *  segments dropped, so stray spaces around commas don't trigger false warnings. */
const normalizeAddr = (s: string | undefined | null): string =>
  (s || '').split(',').map(p => p.trim()).filter(Boolean).join(', ').toLowerCase();

/**
 * Compares an extracted bill against the known onboarding context and returns a
 * warning for each field that doesn't match the parent it's being added under.
 *
 * Each field only fires at the levels where that parent is actually pinned:
 * LEN at LEN / SERVICE_ADDRESS / METER, service address at SERVICE_ADDRESS /
 * METER, and UAN at METER (where `context.utilityAccountNumber` identifies the
 * one meter the bill is being added to). Above those levels there's no single
 * entity to compare against.
 */
export function detectContextMismatches(
  context: OnboardingContext | null,
  nav: BillNavEntry | null,
): MismatchWarning[] {
  const warnings: MismatchWarning[] = [];
  if (!context || !nav) return warnings;

  const atSaOrMeter = context.level === 'SERVICE_ADDRESS' || context.level === 'METER';
  const atLenOrBelow = context.level === 'LEN' || atSaOrMeter;

  // Extracted LEN vs known LEN
  if (atLenOrBelow && context.lenName) {
    const extracted = normalize(nav.lenName);
    const known = normalize(context.lenName);
    if (extracted && known && extracted !== known && extracted !== 'unknown entity') {
      warnings.push({
        field: 'legalEntity',
        fieldLabel: 'Legal Entity',
        expectedValue: context.lenName,
        extractedValue: nav.lenName,
        severity: 'warning',
      });
    }
  }

  // Extracted SA vs known SA
  if (atSaOrMeter && context.serviceAddress) {
    const parts = [context.serviceAddress.street1, context.serviceAddress.city, context.serviceAddress.state]
      .filter(Boolean);
    const known = normalizeAddr(parts.join(', '));
    const extracted = normalizeAddr(nav.saAddress);
    if (extracted && known && extracted !== known && extracted !== 'unknown address') {
      warnings.push({
        field: 'serviceAddress',
        fieldLabel: 'Service Address',
        expectedValue: parts.join(', '),
        extractedValue: nav.saAddress,
        severity: 'warning',
      });
    }
  }

  // Extracted UAN vs known meter UAN — METER level only.
  // nav.meterUan falls back to 'Unknown' when the bill carried no UAN; treat
  // that as "nothing to compare", same as the LEN 'unknown entity' guard.
  if (context.level === 'METER' && context.utilityAccountNumber) {
    const extracted = normalize(nav.meterUan);
    const known = normalize(context.utilityAccountNumber);
    if (extracted && known && extracted !== known && extracted !== 'unknown') {
      warnings.push({
        field: 'utilityAccountNumber',
        fieldLabel: 'Utility Account Number',
        expectedValue: context.utilityAccountNumber,
        extractedValue: nav.meterUan,
        severity: 'warning',
      });
    }
  }

  return warnings;
}
